import ApiService, { ACCOUNTS_API_HOST } from './ApiService';
import { download } from './helpers';
import { Capacitor } from '@capacitor/core';

/**
 * @override
 */
class OperationsService extends ApiService {
    /**
     * constructor
     */
    constructor() {
        super({ baseURL: ACCOUNTS_API_HOST });
    }
    /**
     * Get operations list
     * @param {Object} sort Sort
     * @param {Object} config Axios config
     * @return {Object} Response with list of operations
     */
    getOperations(sort, config) {
        if (sort.trim() !== '') {
            return this.getList(`/operations?$orderby=${sort}`, config);
        } else {
            return this.getList('/operations', config);
        }
    }
    /**
     * Get operation
     * @param {String} uuid Operation UUID
     * @param {Object} config Axios config
     * @return {Object} Response with operation
     */
    getOperationsByUUId(uuid, config) {
        return this.get(`/operations/${uuid}`, config);
    }
    /**
     * Get operation suboperations
     * @param {String} uuid Operation UUID
     * @param {Object} config Axios config
     * @return {Object} Response with operation
     */
    getOperationSuboperations(uuid, config) {
        return this.get(`/operations/${uuid}/operations`, config);
    }
    /**
     * @param {String} url Url
     * @param {Object} config Axios config
     * @return {Object} Response with list of operations
     */
    getPagination(url, config) {
        return this.get(`${url}`, config);
    }

    /**
     * Buscar operaciones
     * @param {Array} search Búsqueda
     * @param {Object} config Axios config
     * @return {Object} Response with opertaions
     */
    getSearch(search, config) {
        return this.get(`/operations?$limit=20&$offset=0&$search=${search}`, config);
    }

    /**
     * Get operations Documents
     * @param {String} uuid Operation UUID
     * @param {Object} config Axios config
     * @return {Object} Response with operation documents data
     */
    getOperationDocuments(uuid, config) {
        return this.getList(`/operations/${uuid}/documents`, config);
    }

    /**
     * Get specific operation document
     * @param {String} uuid Operation UUID
     * @param {String} uuidDocument Document UUID
     */
    getDocument(uuid, uuidDocument) {
        return this.get(`/operations/${uuid}/documents/${uuidDocument}`, {
            responseType: 'blob'
        }).then(res => res);
    }
    /**
     * Get operations historic
     * @param {String} uuid Operation UUID
     * @param {Object} config Axios config
     * @return {Object} Response with operation historic data
     */
    getOperationHistoric(uuid, config) {
        return this.getList(`/operations/${uuid}/historical`, config);
    }
    /**
     * Get operation interveners
     * @param {String} uuid Operation UUID
     * @param {Object} config Axios config
     * @return {Object} Response with operation intervenes data
     */
    getOperationInterveners(uuid, config) {
        return this.getList(`/operations/${uuid}/interveners`, config);
    }
    /**
     * Get operation Issues
     * @param {String} uuid operation UUID
     * @param {Object} config Axios config
     * @return {Object} Response with operation issues data
     */
    getOperationIssues(uuid, config) {
        return this.getList(`/operations/${uuid}/issues`, config);
    }
    /**
     * Get operation Types list
     * @param {Object} sort Sort
     * @param {Object} config Axios config
     * @return {Object} Response with operation issues data
     */
    getOperationTypes(sort, config) {
        if (sort.trim() !== '') {
            return this.getList(`/operation-type?$orderby=${sort}`, config);
        } else {
            return this.getList('/operation-type', config);
        }
    }
    /**
     * Buscar tipo de operacion
     * @param {Array} search Búsqueda
     * @param {Object} config Axios config
     * @return {Object} Response with operation-types
     */
    getSearchOperationType(search, config) {
        return this.getList(`/operation-type?$search=${search}`, config);
    }

    /**
     * Create an operation
     * @param {Object} body body of request
     * @return {Object} Response data
     */
    createOperation(body) {
        if (Capacitor.isNativePlatform()) {
            body = { ...body, is_from_app: true };
        }

        return this.post('/operations', body);
    }

    /**
     * Update operation
     * @param {String} uuid UUID of operation
     * @param {Object} payload modified properties
     * @return {Object} Response data
     */
    updateOperation(uuid, payload) {
        return this.patch(`/operations/${uuid}`, payload);
    }

    /**
     * Update operation
     * @param {String} uuid UUID of operation
     * @param {Object} payload modified properties
     * @return {Object} Response data
     */
    setOperationPaymentLog(uuid, payload) {
        return this.post(`/pay-in/log/${uuid}`, payload);
    }

    /**
     * Delete operation
     * @param {String} uuid Operation id
     * @return {Object} Response of deleted operation
     */
    deleteByUUIId(uuid) {
        return this.delete(`/operations/${uuid}`, {});
    }

    /**
     * @override
     */
    download(sort, config, headers) {
        if (sort.trim() !== '') {
            return this.getDownload(`/operations/download?$orderby=${sort}`, config, { headers });
        } else {
            return this.getDownload('/operations/download', config, { headers });
        }
    }

    /**
     * Get account Operations
     * @param {String} uuid Account UUID
     * @param {Object} config Axios config
     * @return {Object} Response with account operations data
     */
    getRelatedOperations(uuid, config) {
        return this.getList(`/accounts/${uuid}/operations`, config);
    }

    /**
     * Change operation status
     * @param {String} uuid Operation UUID
     * @param {Object} body body of request
     * @return {Object} Response with opertaion changed
     */
    changeOperationStatus(uuid, body) {
        return this.post(`/operations/${uuid}/status-change`, body);
    }

    /**
     * @param {String} uuid  UUID Operation
     * @param {String} uuidDocuemnt Document UUID
     * @param {Object} config Axios config
     * @return {Object} Response with intervener legal data
     */
    removeDocument(uuid, uuidDocument, config) {
        return this.delete(`/operations/${uuid}/documents/${uuidDocument}`, config);
    }

    /**
     * @override
     */
    upload(payload) {
        const config = {
            headers: {
                'Content-Type': 'multipart/form-data'
            }
        };

        const formData = new FormData();
        for (let i = 0; i < payload.files.length; i++) {
            formData.append('attachment', payload.files[i]);
            formData.append('filename', payload.filesName[i]);
            formData.append(
                'category',
                typeof payload.category === 'string' ? payload.category : payload.category[i]
            );
            formData.append(
                'subcategory',
                typeof payload.subcategory === 'string'
                    ? payload.subcategory
                    : payload.subcategory[i]
            );
            formData.append('validated', payload.validated);
        }
        return this.post(`/operations/${payload.uuid}/documents`, formData, config);
    }

    /**
     * @param {String} uuid Operation UUID
     * @param {Object} config Axios config
     * @return {Object} Response with intervener documents data
     */
    getDocumentsValidations(uuid, config) {
        return this.getList(`/operations/${uuid}/document-validations`, config);
    }

    /**
     * Change document objects
     * @param {String} uuid UUID operation
     * @param {String} uuidDocument UUID document
     * @param {Object} payload modified properties
     * @return {Object} Response data
     */
    updateDocument(uuid, uuidDocument, payload) {
        return this.patch(`/operations/${uuid}/documents/${uuidDocument}`, payload);
    }

    /**
     * Update periodic operation info
     * @param {String} uuid UUID of operation
     * @param {Object} payload modified properties
     * @return {Object} Response data
     */
    updatePeriodicOperation(uuid, payload) {
        return this.patch(`/operations/${uuid}`, payload);
    }

    /**
     * Send operation to RD
     * @param {String} uuid UUID of operation
     * @return {Object} Response data
     */
    sendToRD(uuid) {
        return this.post(`/operations/${uuid}/export-to-rd`);
    }

    /**
     * Download file to export to RD
     * @param {String} uuid UUID of operation
     * @param {String} filename Filename
     * @return {Object} Response data
     */
    downloadOperationsByUUId(uuid, filename = null) {
        return this.post(
            `/operations/${uuid}/export-to-rd-txt`,
            {},
            { responseType: 'arraybuffer' }
        ).then(response => download(response, filename));
    }

    /**
     * Download RD file
     * @param {String} mimetype mime type
     * @param {String} filename Filename
     * @param {Object} config Axios config
     * @return {Object} Response data
     */
    downloadOperations(mimetype, filename, config) {
        return this.post('/operations/export-to-rd-txt', config, {
            responseType: 'arraybuffer'
        }).then(response => download(response, filename));
    }

    /**
     * Imports operations from RD
     * @return {Object} Response data
     */
    importRDOperations() {
        return this.post('/operations/export-to-rd-txt', {}, { responseType: 'arraybuffer' });
    }

    /**
     * Get payIn widget for operation payment
     * @param {Object} operation operation object
     * @return {Object} Response data
     */
    getPaymentIframe(payload) {
        return this.post('/pay-in', payload);
    }

    /**
     * Get log for operation payment
     * @param {String} uuid UUID of operation
     * @return {Object} Response data
     */
    getPaymentLog(uuid) {
        return this.get(`pay-in/log/${uuid}`);
    }

    /**
     * Get cancel widget for operation payment cancelation
     * @param {String} uuid UUID of operation
     * @return {Object} Response data
     */
    getCancelationIframe(uuid) {
        return this.get(`/pay-in/${uuid}/cancel`);
    }

    /**
     * Get payIn banks allowed for periodic suscriptions
     * @param {Object} operation operation object
     * @return {Object} Response data
     */
    getAllowedBanks() {
        return this.get('/pay-in/banks');
    }

    /**
     * Get FI transfers data from DDBB
     * @param {Object} config configuration object
     * @return {Object} Response with cnmv data
     */
    getFITransferData(config) {
        return this.getList('/transfer-data/fi', config);
    }

    /**
     * Get FI transfers data from DDBB
     * @param {Object} config configuration object
     * @return {Object} Response with cnmv data
     */
    getPPTransferData(config) {
        return this.getList('/transfer-data/pp', config);
    }

    /**
     * Get FI transfers data from DDBB
     * @param {Object} config configuration object
     * @return {Object} Response with cnmv data
     */
    getPPATransferData(config) {
        return this.getList('/transfer-data/ppa', config);
    }

    /**
     *
     * @param {String} uuid UUID of operation
     * @param {*} uuidDocument
     * @returns {Object} Promise
     */
    downloadOperationDocument(uuid, uuidDocument) {
        return this.get(`/operations/${uuid}/documents/${uuidDocument}`, {
            responseType: 'blob'
        });
    }

    /**
     * Get operation Fiscal Items
     * @param {String} uuid operation UUID
     * @param {Object} config Axios config
     * @return {Object} Response with operation issues data
     */
    getOperationFiscalItems(uuid, config) {
        return this.getList(`/operations/${uuid}/fiscal-items`, config);
    }
}

const operationsService = new OperationsService();

export default operationsService;
